/* ================================================================== */
/* METEOR — ability three                                              */
/* ================================================================== */
/**
 * A burning rock lobbed along the aimed line, which detonates on arrival.
 *
 * The rock is real geometry — a cratered, faceted asteroid generated by
 * `assets/ProceduralGeometry.js` — shaded by a patched standard material so it
 * casts and receives the stage's shadows. Its signature is the **lava seams**:
 * the zero crossing of an fbm field sampled in the rock's own local space, so
 * the cracks are welded to it and tumble with it. `chargeCurve` decides how
 * fast they prise open on the way in.
 *
 * Behind it hangs the **fire trail**: a black-body volume raymarched inside a
 * camera-facing proxy hull laid along the arc. See the `trail*` block.
 *
 * As in `ice` and `thunder`, a cast captures nothing but dice and timestamps:
 * one seed, one tumble axis and a few unitless rolls per debris chunk. The
 * trajectory, the size of the rock, the width of its seams and the whole
 * ballistic flight of every chunk are resolved against this block each frame —
 * which is why dragging `arc` re-lofts a meteor already in the air, and
 * dragging `chunkSpeed` re-throws debris that has already landed.
 */
export const meteor = {
  /* --- the cast --- */
  range: 20.0, // maximum cast distance, metres
  minRange: 3.0, // closer than this and the cast is refused
  speed: 21.0, // how fast the rock travels downrange, metres/second
  lifetime: 2.2, // seconds the crater burns after the impact
  fadeTime: 1.6, // seconds everything takes to clear
  cooldown: 0.9,
  castAnim: 'cast1', // which clip in `CAST_ANIMATIONS` the body throws

  /* --- the flight path --- */
  // The rock is thrown from a hand, so these are measured from the caster's
  // origin in the cast's own frame.
  handHeight: 1.35, // metres above the floor
  handForward: 0.6, // metres in front of the caster
  handSide: 0.2, // metres to the side (+ follows `Ability#side`)
  endHeight: 0.75, // height of the rock where it lands, metres
  arc: 2.6, // metres the mid-span lobs upward
  arcCurve: 0.85, // <1 flattens the top of the arc, >1 peaks it

  /* --- the rock --- */
  radius: 0.8, // metres
  facets: 3, // icosphere subdivisions, 0–3 (3 = 1280 triangles)
  lumpiness: 0.26, // low-frequency deformation, × the radius
  lumpScale: 1.5, // lumps per unit radius
  surfaceRoughness: 0.16, // high-frequency chipping
  cuts: 9, // planar fracture faces sliced off it
  cutDepth: 0.28, // how far in those planes bite, × the radius
  craters: 5, // impact bowls punched into it
  craterDepth: 0.18, // how deep those bowls go, × the radius
  craterSize: 0.5, // their angular radius, radians
  spin: 3.4, // tumble rate, radians/second

  /* --- the lava seams --- */
  chargeCurve: 1.6, // how late the rock heats up on its way in
  crackScale: 0.95, // seams per unit radius
  crackWidth: 0.045, // how wide a seam opens (doubled at full charge)
  crackBranches: 0.5, // strength of the finer seams splitting off
  crackGlow: 2.2,
  crackFlow: 0.7, // how much the magma brightness crawls
  crackFlowSpeed: 0.9,
  rockScale: 3.4, // mottling of the rock between the seams
  facetTint: 0.5, // per-facet value break-up — what makes it read as stone
  cavity: 0.25, // darkening down in the craters and the cut faces
  soot: 0.6, // charring either side of a seam
  rimHeat: 0.7, // heat sheath around the silhouette
  leadGlow: 0.9, // compression heat on the leading facets
  leadSharp: 2.6, // how tightly that hugs the nose
  glow: 0.75, // overall emissive gain
  envIntensity: 1.25, // how much of the HDR probe the rock catches
  colorRock: '#6e675f',
  colorChar: '#17130f',
  colorCrack: '#ff6a12',
  colorHot: '#fff3d0',

  /* --- the fire trail --- */
  /**
   * The burning wake, **raymarched as a black-body volume** — the firebending
   * stream from the freehand sandbox, re-aimed at the meteor's arc. The mesh
   * drawn is only a camera-facing proxy hull; the flame itself is integrated
   * inside it by `materials/VolumetricFireMaterial.js`, which is where the four
   * layers these controls drive (silhouette → vortex roll-up → turbulence →
   * shred) are explained.
   *
   * As with everything else here it is not a recorded history: the hull's
   * centre line is sampled straight off the trajectory, so these reshape fire
   * that is already in the air.
   *
   * The volume borrows the rock's palette — `colorHot`, `colorFlameMid`,
   * `colorFlameEdge`, `colorFlameSmoke` — but only reaches for it in
   * proportion to `trailPalette`; at 0 it is a pure Planckian radiator and the
   * colour comes out of `trailTempCore` / `trailTempEdge` instead.
   */
  trailSpan: 7.0, // metres of arc the fire covers behind the rock
  trailWidth: 0.66, // tube radius, metres
  trailHeadSize: 1.8, // fireball radius at the rock, × trailWidth
  trailPlume: 1.1, // upward stretch of the volume (buoyant elongation)
  trailWakeSpread: 0.22, // how far the spent gas behind the head has ballooned
  trailRise: 0.35, // how far the far end of the wake has floated upward, metres
  // Metre-scale lobes in the silhouette. Without these the outline stays a
  // capsule no matter how much fine turbulence is piled on top of it, and the
  // trail reads as a shaded tube.
  trailBulge: 0.18, // how far those lobes swell and pinch the local radius
  trailBulgeScale: 0.34, // lobes per metre — lower = bigger, slower shapes
  // Ring vortices shed off the head and travelling back down the wake. This is
  // what folds the field into curling, mushrooming billows; fbm alone can only
  // make clouds.
  trailVortex: 0.0, // roll-up strength
  trailRingFrequency: 0.0, // vortices per metre of stream
  trailRingSpeed: 4.0, // how fast they travel backwards
  // Kept low on purpose: rolling the noise frame hard around the axis wraps
  // the filaments circumferentially and the flame reads as concentric contour
  // lines rather than as tongues running along the flow.
  trailCurl: 0.0, // swirl of the density field around the axis
  trailTurbulence: 2.94, // noise amplitude eating into the volume
  trailWarp: 0.45, // domain warp — folds the noise into curling sheets
  trailTongue: 0.94, // < 1 stretches structures upward into licking tongues
  trailStreamStretch: 1.13, // < 1 draws them out along the flow
  // Radial shear: how far the fringe is dragged up and back relative to the
  // axis. This is what makes the edge structures read as licking tongues
  // rather than as blobs of the same shape at every radius.
  trailLick: 3.1,
  trailWisps: 0.81, // ridged filaments shredding the fringe into strands
  trailShred: 1.57, // how violently the fringe tears compared to the core
  trailOctaves: 5, // turbulence octaves (quality ↔ cost)
  trailSpeed: 4.62, // how fast the field streams backwards along the path
  trailBuoyancy: 3.5, // how fast it climbs inside the volume
  trailDetachment: 0.9, // how hard the tail tears into separate puffs
  trailNoiseStrength: 0.78,
  trailNoiseFrequency: 3.23,
  trailSoftness: 0.42, // 0 = hard tongues, 1 = a soft glow
  trailFlicker: 0.74,
  trailDensity: 2.09,
  trailSoot: 1.42, // absorption — how much the cool gas occludes
  trailCoreClarity: 0.54, // extinction left in the hottest gas (low = white blob)
  trailSteps: 35, // raymarch samples per pixel (quality ↔ cost)
  trailGlow: 3.06,
  trailOpacity: 0.96,
  trailTailFade: 0.71, // fraction of the trail that has already burnt out
  trailBurnout: 1.2, // seconds the trail takes to die after the impact
  // Temperature & radiance. The flame is shaded as a Planckian radiator: these
  // are the two ends of its temperature range in kelvin, and the exponent the
  // emitted power follows. 4 would be Stefan-Boltzmann; a little gentler keeps
  // the mid-tones off the floor at this exposure.
  trailTempCore: 1920,
  trailTempEdge: 1590,
  trailEmissionCurve: 4.79,
  trailHeatFocus: 1.54, // how fast the gas reaches full heat inside the surface
  trailHeatFalloff: 2.46, // how sharply it cools toward that surface
  // How far the turbulence is allowed to drag the temperature profile around.
  // Radiated power goes as a high power of T, so this number is amplified
  // several-fold on screen — past ~0.5 the noise's own contour lines start
  // showing through as agate banding.
  trailHeatFollow: 0.26,
  trailTailHeat: 0.36, // temperature of the spent gas at the far end of the wake
  trailPalette: 0.62, // 0 = pure black-body physics, 1 = the colour stops below
  trailScatter: 1.99, // firelight bouncing inside the sooty fringe
  trailScatterFalloff: 4.4, // how fast that bath dies away from the core
  colorFlameMid: '#ffb02e',
  colorFlameEdge: '#ff3d10',
  colorFlameSmoke: '#181616',

  /* --- the debris the rock breaks into --- */
  chunkCount: 18, // chunks thrown at the impact (capped at 28)
  chunkScale: 0.28, // their radius, × the meteor's
  chunkSpeed: 7.5, // metres/second they leave the crater at
  chunkForward: 0.55, // how far the spray is biased downrange
  chunkLoft: 1.0, // how steeply they are thrown
  chunkGravity: -17.0,
  chunkSpin: 6.0, // tumble rate, radians/second
  chunkCool: 2.6, // seconds a chunk's seams take to go out
  chunkLinger: 0.5, // seconds they lie there before sinking
  chunkSink: 1.0, // seconds to withdraw into the floor

  /* --- embers, sparks, smoke and grit --- */
  /**
   * As in `ice` and `thunder`: each system is coloured by a four-stop gradient
   * sampled over the particle's own lifetime, `A` at birth through `D` as it
   * dies. Spelled out rather than derived from the flame palette, so the
   * trail can be cooled to red while the rock itself stays white-hot.
   */
  emberRate: 180, // embers streaming off the rock, particles/second
  emberSize: 0.1,
  emberSpeed: 2.4,
  emberLifetime: 1.5,
  emberRise: 1.5, // buoyancy, metres/second
  emberGlow: 1.2,
  emberTurbulence: 0.5,
  colorEmberA: '#fff3d0',
  colorEmberB: '#ff9a2e',
  colorEmberC: '#ff3b0d',
  colorEmberD: '#2b0d05',
  sparkRate: 110, // sparks flung off it
  sparkSize: 0.14,
  sparkSpeed: 6.5,
  sparkLifetime: 0.8,
  sparkGravity: -11.0,
  sparkStretch: 0.16, // how far a spark smears along its velocity
  colorSparkA: '#fffdf2',
  colorSparkB: '#ffd27a',
  colorSparkC: '#ff6a12',
  colorSparkD: '#3d1103',
  smokeRate: 70, // the trail and the column off the crater
  smokeSize: 1.1,
  smokeSpeed: 1.2,
  smokeLifetime: 3.0,
  smokeOpacity: 0.12,
  smokeRise: 0.9,
  colorSmokeA: '#6b503f',
  colorSmokeB: '#3b2c25',
  colorSmokeC: '#241b17',
  colorSmokeD: '#141010',
  debrisSize: 0.06, // grit kicked off the floor
  debrisSpeed: 6.0,
  debrisLifetime: 1.5,
  debrisGravity: -18.0,
  colorDebrisA: '#3a322c',
  colorDebrisB: '#2a231e',
  colorDebrisC: '#1c1714',
  colorDebrisD: '#151110',

  /* --- the molten cracks torn through the floor --- */
  /**
   * Real geometry, not a decal: arms of crack that meander outward from the
   * impact, shed branches, glow from a white-hot core through a wide orange
   * underglow, and heave basalt up along their lips. See
   * `effects/GroundFissures.js` — the network is baked in a unit disc, so
   * `fissureRadius` re-scales cracks that are already on the ground.
   */
  fissureRadius: 5.2, // how far the cracks reach, metres
  fissureLife: 6.5, // seconds before they close up
  fissureArms: 6, // main cracks radiating from the impact
  fissureWander: 1.6, // how hard an arm veers, radians per unit walked
  fissureBranches: 0.75, // fraction of the generated branches kept, 0..1
  fissureBranchLength: 0.85, // how far along a branch runs before its point, 0..1
  fissureWidth: 0.14, // width of the open seam, metres
  fissureHeat: 1.5, // core temperature
  fissurePulse: 1.0, // speed of the heat waves travelling along them
  fissureGrowth: 9.0, // how fast the cracks race outward, metres/second
  fissureRockSize: 0.3, // basalt heaved up along the lips, metres

  /* --- what else the ground does --- */
  scorchRadius: 2.8, // burnt patch under it, metres
  scorchLife: 8.0,
  scorchIntensity: 0.95,
  shockRadius: 6.0, // impact shockwave ring, metres
  colorScorch: '#0d0907',
  colorShockA: '#ff9a2e', // body of the shockwave ring
  colorShockB: '#fff3d0', // its crest

  /* --- dynamic light --- */
  lightIntensity: 16,
  lightRadius: 14,
  lightColor: '#ff8a3c',
  lightFlicker: 0.25, // depth of the light's gutter, 0 = steady
  lightFlickerSpeed: 13,

  /* --- the launch and the detonation --- */
  muzzleSize: 0.0, // the flare at the hand as the rock leaves it — 0 = none
  muzzleIntensity: 1.6,
  castFlash: 0.08, // screen flash on release
  colorCastFlash: '#ff9a2e',
  burstSize: 3.6, // the fireball at the impact point, metres
  burstIntensity: 1.0,
  burstTurbulence: 2.0, // how hard the noise eats into the fireball's shell
  burstEmbers: 260, // extra embers thrown at the impact
  burstSparks: 180,
  burstDebris: 90,
  burstSmoke: 70,
  impactShake: 1.0,
  shakeDuration: 1.1,
  impactFlash: 0.3,
  rumble: 0.04, // continuous shake while the rock is in the air
  colorFlash: '#ff9a2e' // the full-screen flash on impact
};

/* ------------------------------------------------------------------ */
/* Editor layout                                                       */
/* ------------------------------------------------------------------ */
/**
 * Cinder Fall.
 *
 * The seven controls under "The rock" regenerate real geometry — see
 * `MeteorAbility#_syncGeometry` — and everything else is read by a shader or
 * resolved from scratch on the frame it changes, so the whole folder reshapes
 * a meteor that is already in the air. The ones worth reaching for first are
 * `arc` (how hard it is lobbed), `crackWidth` and `chargeCurve` (how the lava
 * seams open on the way in), `trailSpan` and `trailWidth` (how much fire
 * streams off it) and `chunkSpeed` (how far the wreckage is thrown).
 *
 * The trail's four sub-folders are spelled with `/` in the folder name: a
 * schema is a flat map, and the slash is how it declares nesting.
 */
export const meteorSchema = {
  'The cast': [
    ['range', 2, 60, 0.1, 'max range'],
    ['minRange', 0, 10, 0.1, 'min range'],
    ['speed', 3, 90, 0.5, 'travel speed'],
    ['lifetime', 0.2, 10, 0.1, 'crater lifetime'],
    ['fadeTime', 0.1, 6, 0.05, 'clear-out time'],
    ['cooldown', 0, 6, 0.05, 'cooldown'],
    ['castAnim', 'cast animation']
  ],
  'The flight path': [
    ['handHeight', 0, 3, 0.01, 'hand height'],
    ['handForward', -1, 3, 0.01, 'hand forward'],
    ['handSide', -1.5, 1.5, 0.01, 'hand lateral'],
    ['endHeight', 0, 4, 0.01, 'height at target'],
    ['arc', -4, 12, 0.05, 'lob height'],
    ['arcCurve', 0.1, 4, 0.01, 'lob curve']
  ],
  // Everything down to `craterSize` rebuilds the asteroid geometry. `cuts` is
  // the one that decides whether it reads as stone: it slices flat fracture
  // faces off the ball, which no amount of noise can fake.
  'The rock': [
    ['radius', 0.05, 3, 0.01, 'radius'],
    ['facets', 0, 3, 1, 'subdivisions'],
    ['lumpiness', 0, 0.8, 0.01, 'lumpiness'],
    ['lumpScale', 0.2, 6, 0.05, 'lumps / radius'],
    ['surfaceRoughness', 0, 1, 0.01, 'surface roughness'],
    ['cuts', 0, 16, 1, 'fracture faces'],
    ['cutDepth', 0, 0.5, 0.01, 'fracture depth'],
    ['craters', 0, 14, 1, 'craters'],
    ['craterDepth', 0, 0.6, 0.01, 'crater depth'],
    ['craterSize', 0.05, 1.4, 0.01, 'crater size'],
    ['spin', -20, 20, 0.1, 'tumble rate']
  ],
  'Lava seams': [
    ['chargeCurve', 0.1, 5, 0.01, 'heat-up curve'],
    ['crackScale', 0.3, 10, 0.05, 'seams / radius'],
    ['crackWidth', 0.005, 0.5, 0.005, 'seam width'],
    ['crackBranches', 0, 1.5, 0.01, 'branch seams'],
    ['crackGlow', 0, 10, 0.05, 'seam glow'],
    ['crackFlow', 0, 1, 0.01, 'magma crawl'],
    ['crackFlowSpeed', 0, 5, 0.01, 'crawl speed'],
    ['rockScale', 0.2, 10, 0.05, 'rock mottling'],
    ['facetTint', 0, 1.2, 0.01, 'per-facet tint'],
    ['cavity', 0, 1, 0.01, 'cavity shading'],
    ['soot', 0, 1.5, 0.01, 'soot around seams'],
    ['rimHeat', 0, 4, 0.01, 'heat sheath'],
    ['leadGlow', 0, 6, 0.01, 'leading-face heat'],
    ['leadSharp', 0.5, 8, 0.05, 'leading-face falloff'],
    ['glow', 0, 4, 0.01, 'glow'],
    ['envIntensity', 0, 3, 0.01, 'reflection'],
    ['colorRock', 'rock'],
    ['colorChar', 'char'],
    ['colorCrack', 'seam'],
    ['colorHot', 'white hot']
  ],
  // The trail is a raymarched volume, so these are volume parameters, not
  // surface ones — see `materials/VolumetricFireMaterial.js`. `trailWidth`,
  // `trailPlume` and `trailSpan` set its shape; `trailSteps` is the cost dial.
  'The fire trail': [
    ['trailSpan', 0.5, 30, 0.1, 'trail length'],
    ['trailWidth', 0.02, 2, 0.01, 'tube radius'],
    ['trailHeadSize', 0.5, 5, 0.01, 'head size'],
    ['trailPlume', 0.3, 4, 0.01, 'upward stretch'],
    ['trailWakeSpread', 0, 3, 0.01, 'wake spread'],
    ['trailRise', 0, 3, 0.01, 'wake rise'],
    ['trailDetachment', 0, 1.5, 0.01, 'tail break-up'],
    ['trailSoftness', 0.05, 1, 0.01, 'surface softness'],
    ['trailBurnout', 0.05, 4, 0.05, 'burn-out time'],
    ['trailTailFade', 0.01, 0.8, 0.01, 'tail burn-out']
  ],
  // Metre-scale lobes. Without these the outline stays a capsule no matter how
  // much fine turbulence is piled on top of it.
  'The fire trail/Silhouette': [
    ['trailBulge', 0, 1, 0.01, 'lobe depth'],
    ['trailBulgeScale', 0.05, 2, 0.01, 'lobes / metre'],
    ['trailShred', 0, 4, 0.01, 'fringe shred'],
    ['trailWisps', 0, 2, 0.01, 'wisps'],
    ['trailLick', 0, 8, 0.05, 'radial shear']
  ],
  'The fire trail/Motion & turbulence': [
    ['trailSpeed', 0, 12, 0.01, 'flow speed'],
    ['trailBuoyancy', 0, 10, 0.01, 'buoyancy'],
    ['trailTurbulence', 0, 8, 0.01, 'turbulence'],
    ['trailNoiseStrength', 0, 4, 0.01, 'noise strength'],
    ['trailNoiseFrequency', 0.1, 10, 0.01, 'noise frequency'],
    ['trailWarp', 0, 1.5, 0.01, 'domain warp'],
    ['trailCurl', 0, 3, 0.01, 'axial swirl'],
    ['trailVortex', 0, 2, 0.01, 'vortex roll-up'],
    ['trailRingFrequency', 0, 3, 0.01, 'rings / metre'],
    ['trailRingSpeed', 0, 10, 0.05, 'ring speed'],
    ['trailTongue', 0.2, 3, 0.01, 'tongue stretch'],
    ['trailStreamStretch', 0.2, 3, 0.01, 'streamwise stretch'],
    ['trailFlicker', 0, 2, 0.01, 'flicker'],
    ['trailOctaves', 1, 5, 1, 'detail octaves']
  ],
  // The flame is shaded as a Planckian radiator: colour comes out of the
  // temperature. `trailPalette` blends toward the hand-authored stops instead.
  'The fire trail/Temperature & radiance': [
    ['trailTempCore', 1000, 5000, 10, 'core temperature (K)'],
    ['trailTempEdge', 1000, 4000, 10, 'edge temperature (K)'],
    ['trailEmissionCurve', 1, 6, 0.01, 'radiance exponent'],
    ['trailHeatFocus', 0.05, 3, 0.01, 'heat focus'],
    ['trailHeatFalloff', 0.05, 4, 0.01, 'heat falloff'],
    ['trailHeatFollow', 0, 1, 0.01, 'heat follows noise'],
    ['trailTailHeat', 0, 1, 0.01, 'spent-gas heat'],
    ['trailScatter', 0, 4, 0.01, 'scatter'],
    ['trailScatterFalloff', 0.2, 8, 0.05, 'scatter falloff'],
    ['trailPalette', 0, 1, 0.01, 'palette vs physics'],
    ['colorFlameMid', 'flame mid'],
    ['colorFlameEdge', 'flame edge'],
    ['colorFlameSmoke', 'flame smoke']
  ],
  'The fire trail/Volume rendering': [
    ['trailDensity', 0, 6, 0.01, 'density'],
    ['trailSoot', 0, 5, 0.01, 'soot absorption'],
    ['trailCoreClarity', 0, 1, 0.01, 'core clarity'],
    ['trailGlow', 0, 8, 0.01, 'glow'],
    ['trailOpacity', 0, 2, 0.01, 'opacity'],
    ['trailSteps', 6, 72, 1, 'raymarch steps']
  ],
  'The wreckage': [
    ['chunkCount', 0, 28, 1, 'chunks'],
    ['chunkScale', 0.05, 0.8, 0.01, 'chunk size'],
    ['chunkSpeed', 0, 30, 0.1, 'throw speed'],
    ['chunkForward', 0, 2, 0.01, 'downrange bias'],
    ['chunkLoft', 0, 1.5, 0.01, 'loft'],
    ['chunkGravity', -50, -1, 0.1, 'gravity'],
    ['chunkSpin', 0, 20, 0.1, 'tumble rate'],
    ['chunkCool', 0.1, 8, 0.05, 'cool-down time'],
    ['chunkLinger', 0, 4, 0.05, 'hold before sinking'],
    ['chunkSink', 0.1, 4, 0.05, 'sink time']
  ],
  'Embers & sparks': [
    ['emberRate', 0, 900, 1, 'ember rate'],
    ['emberSize', 0.005, 0.5, 0.005, 'ember size'],
    ['emberSpeed', 0, 15, 0.05, 'ember speed'],
    ['emberLifetime', 0.1, 8, 0.05, 'ember lifetime'],
    ['emberRise', -3, 8, 0.05, 'ember rise'],
    ['emberGlow', 0, 4, 0.01, 'ember glow'],
    ['emberTurbulence', 0, 3, 0.01, 'ember turbulence'],
    ['sparkRate', 0, 900, 1, 'spark rate'],
    ['sparkSize', 0.005, 0.8, 0.005, 'spark size'],
    ['sparkSpeed', 0, 40, 0.1, 'spark speed'],
    ['sparkLifetime', 0.05, 4, 0.01, 'spark lifetime'],
    ['sparkGravity', -50, 5, 0.1, 'spark gravity'],
    ['sparkStretch', 0, 3, 0.01, 'spark stretch'],
    ['colorEmber*', 'Ember colour'],
    ['colorSpark*', 'Spark colour']
  ],
  'Smoke & grit': [
    ['smokeRate', 0, 500, 1, 'smoke rate'],
    ['smokeSize', 0.05, 4, 0.01, 'smoke size'],
    ['smokeSpeed', 0, 8, 0.05, 'smoke speed'],
    ['smokeLifetime', 0.2, 10, 0.05, 'smoke lifetime'],
    ['smokeOpacity', 0, 1, 0.005, 'smoke opacity'],
    ['smokeRise', -2, 5, 0.01, 'smoke rise'],
    ['debrisSize', 0.005, 0.4, 0.005, 'grit size'],
    ['debrisSpeed', 0, 25, 0.1, 'grit speed'],
    ['debrisLifetime', 0.1, 5, 0.05, 'grit lifetime'],
    ['debrisGravity', -50, 0, 0.1, 'grit gravity'],
    ['colorSmoke*', 'Smoke colour'],
    ['colorDebris*', 'Grit colour']
  ],
  'Molten cracks': [
    ['fissureRadius', 0.5, 16, 0.05, 'reach'],
    ['fissureLife', 0.5, 25, 0.1, 'lifetime'],
    ['fissureArms', 2, 12, 1, 'main cracks'],
    ['fissureWander', 0, 6, 0.05, 'meander'],
    ['fissureBranches', 0, 1, 0.01, 'branch density'],
    ['fissureBranchLength', 0, 1, 0.01, 'branch length'],
    ['fissureWidth', 0.01, 1, 0.005, 'seam width'],
    ['fissureHeat', 0, 4, 0.01, 'core heat'],
    ['fissurePulse', 0, 5, 0.01, 'heat-wave speed'],
    ['fissureGrowth', 0.5, 40, 0.1, 'spread speed'],
    ['fissureRockSize', 0, 1.2, 0.01, 'lip rubble size']
  ],
  'The crater': [
    ['scorchRadius', 0.2, 12, 0.05, 'scorch radius'],
    ['scorchLife', 0.5, 20, 0.1, 'scorch lifetime'],
    ['scorchIntensity', 0, 2, 0.01, 'scorch intensity'],
    ['shockRadius', 0.5, 25, 0.1, 'shockwave radius'],
    ['colorScorch', 'scorch'],
    ['colorShockA', 'shockwave ring'],
    ['colorShockB', 'shockwave crest']
  ],
  'Launch & detonation': [
    ['muzzleSize', 0, 6, 0.05, 'launch flare'], // 0 = no flare
    ['muzzleIntensity', 0, 5, 0.01, 'launch intensity'],
    ['castFlash', 0, 2, 0.01, 'flash on release'],
    ['colorCastFlash', 'release flash colour'],
    ['burstSize', 0.2, 18, 0.05, 'fireball size'],
    ['burstIntensity', 0, 5, 0.01, 'fireball intensity'],
    ['burstTurbulence', 0, 4, 0.01, 'fireball turbulence'],
    ['burstEmbers', 0, 800, 1, 'burst embers'],
    ['burstSparks', 0, 600, 1, 'burst sparks'],
    ['burstDebris', 0, 400, 1, 'burst grit'],
    ['burstSmoke', 0, 300, 1, 'burst smoke'],
    ['impactShake', 0, 3, 0.01, 'shake'],
    ['shakeDuration', 0.1, 4, 0.01, 'shake duration'],
    ['impactFlash', 0, 2, 0.01, 'screen flash'],
    ['rumble', 0, 0.5, 0.005, 'travel rumble'],
    ['colorFlash', 'impact flash colour']
  ],
  'Dynamic light': [
    ['lightIntensity', 0, 120, 0.5, 'light intensity'],
    ['lightRadius', 0.5, 50, 0.1, 'light radius'],
    ['lightFlicker', 0, 1, 0.01, 'light gutter'],
    ['lightFlickerSpeed', 1, 60, 0.5, 'gutter rate'],
    ['lightColor', 'light colour']
  ]
};
